import { isPlatformBrowser, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';

interface Sprite {
  id: string;
  kind: 'flake' | 'drum';
  styles: { [key: string]: string };
  durationMs: number;
  initialOffsetMs: number;
}

const DRUM_COUNT_DESKTOP = 60;
const SNOW_COUNT_DESKTOP = 220;
const BASE_PLAYBACK_RATE = 1;
const BOOSTED_PLAYBACK_RATE = 2.8;

@Component({
  selector: 'loyaltic-snow-layer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './snow-layer.component.html',
  styleUrls: ['./snow-layer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnowLayerComponent implements AfterViewInit, OnDestroy {
  private readonly isBrowser: boolean;
  private readonly prefersReducedMotion: boolean;

  private _boosted = false;
  private initDelayHandle: number | null = null;

  @HostBinding('class.is-boosted')
  get boostedClass(): boolean {
    return this._boosted;
  }

  @Input()
  set boosted(value: boolean) {
    if (this._boosted !== value) {
      this._boosted = value;
      this.updatePlaybackRate();
    }
  }
  get boosted(): boolean {
    return this._boosted;
  }

  private _drumCount = DRUM_COUNT_DESKTOP;
  private _snowCount = SNOW_COUNT_DESKTOP;

  @Input()
  get drumCount(): number {
    return this._drumCount;
  }
  set drumCount(value: number) {
    const next = this.normalizeCount(value, DRUM_COUNT_DESKTOP);
    if (next !== this._drumCount) {
      this._drumCount = next;
      this.rebuildSprites('drum');
    }
  }

  @Input()
  get snowCount(): number {
    return this._snowCount;
  }
  set snowCount(value: number) {
    const next = this.normalizeCount(value, SNOW_COUNT_DESKTOP);
    if (next !== this._snowCount) {
      this._snowCount = next;
      this.rebuildSprites('flake');
    }
  }

  @ViewChildren('drumSprite') private drumElements!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('flakeSprite') private flakeElements!: QueryList<ElementRef<HTMLDivElement>>;

  private animations: Animation[] = [];
  private subscriptions = new Subscription();
  private startY = -120;
  private endY = 1100;
  private fadeOffset = 0.92;
  private viewReady = false;
  private resizeObserver?: ResizeObserver;
  private reinitHandle: number | null = null;

  drumsticks: Sprite[] = [];
  snowflakes: Sprite[] = [];

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private hostRef: ElementRef<HTMLElement>
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.prefersReducedMotion =
      this.isBrowser && typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    this.rebuildSprites('both');
  }

  trackById(_: number, sprite: Sprite): string {
    return sprite.id;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || this.prefersReducedMotion) {
      return;
    }

    this.viewReady = true;
    const runInit = () => {
      this.initAnimations();
      this.initDelayHandle = null;
    };

    if (typeof (window as any).requestIdleCallback === 'function') {
      this.initDelayHandle = (window as any).requestIdleCallback(runInit);
    } else {
      this.initDelayHandle = window.setTimeout(runInit, 150);
    }

    const content = this.getContentContainer();
    if (content && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleReinit());
      this.resizeObserver.observe(content);
    }

    this.subscriptions.add(
      this.drumElements.changes.subscribe(() => this.scheduleReinit())
    );
    this.subscriptions.add(
      this.flakeElements.changes.subscribe(() => this.scheduleReinit())
    );
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleReinit();
  }

  ngOnDestroy(): void {
    this.destroyAnimations();
    this.subscriptions.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.reinitHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.reinitHandle);
      this.reinitHandle = null;
    }
    if (this.initDelayHandle !== null) {
      if (typeof (window as any).cancelIdleCallback === 'function') {
        (window as any).cancelIdleCallback(this.initDelayHandle);
      } else {
        clearTimeout(this.initDelayHandle);
      }
      this.initDelayHandle = null;
    }
    this.viewReady = false;
  }

  private initAnimations(): void {
    if (!this.viewReady) {
      return;
    }

    this.destroyAnimations();
    this.computeMotionBounds();

    const applyAnimations = (elements: QueryList<ElementRef<HTMLDivElement>>, sprites: Sprite[]) =>
      elements.forEach((ref, index) => {
        const sprite = sprites[index];
        if (!sprite) return;
        const animation = this.createAnimation(ref.nativeElement, sprite);
        this.animations.push(animation);
      });

    if (this.drumElements) {
      applyAnimations(this.drumElements, this.drumsticks);
    }
    if (this.flakeElements) {
      applyAnimations(this.flakeElements, this.snowflakes);
    }

    this.updatePlaybackRate();
  }

  private scheduleReinit(): void {
    if (!this.isBrowser || this.prefersReducedMotion || !this.viewReady) {
      return;
    }

    if (typeof requestAnimationFrame === 'undefined') {
      this.initAnimations();
      return;
    }

    if (this.reinitHandle !== null) {
      cancelAnimationFrame(this.reinitHandle);
    }

    this.reinitHandle = requestAnimationFrame(() => {
      this.reinitHandle = null;
      this.initAnimations();
    });
  }

  private computeMotionBounds(): void {
    const hostElement = this.hostRef.nativeElement;
    const wrap = hostElement.parentElement as HTMLElement | null;
    const content = this.getContentContainer();
    const fallbackHeight =
      content?.offsetHeight ??
      wrap?.offsetHeight ??
      (this.isBrowser && typeof window !== 'undefined' ? window.innerHeight : 800);
    const contentHeight = content?.offsetHeight ?? fallbackHeight;

    const entryBuffer = Math.max(80, Math.min(180, contentHeight * 0.12));
    const exitBuffer = Math.max(120, Math.min(240, contentHeight * 0.18));
    const fadeBuffer = Math.max(60, Math.min(180, contentHeight * 0.16));

    this.startY = -entryBuffer;
    this.endY = contentHeight + exitBuffer;

    const travel = this.endY - this.startY || 1;
    let fadeTarget = contentHeight - fadeBuffer;
    fadeTarget = Math.min(fadeTarget, this.endY - exitBuffer * 0.4);
    fadeTarget = Math.max(fadeTarget, this.startY + travel * 0.4);

    this.fadeOffset = (fadeTarget - this.startY) / travel;
    this.fadeOffset = Math.min(0.98, Math.max(0.2, this.fadeOffset));
  }

  private getContentContainer(): HTMLElement | null {
    const wrap = this.hostRef.nativeElement.parentElement as HTMLElement | null;
    if (!wrap) {
      return null;
    }
    return wrap.querySelector<HTMLElement>('.calendar-wrap__content');
  }

  private buildTransform(y: number, rotation: string): string {
    return `translate3d(var(--x, 0), ${y}px, 0) scale(var(--scale, 1)) rotate(${rotation})`;
  }

  private createAnimation(element: HTMLElement, sprite: Sprite): Animation {
    const keyframes: Keyframe[] = [
      {
        transform: this.buildTransform(this.startY, '0deg'),
        opacity: 0,
      },
      { offset: 0.1, opacity: 'var(--opacity, 0.5)' },
      { offset: this.fadeOffset, opacity: 'var(--opacity, 0.5)' },
      {
        transform: this.buildTransform(this.endY, 'var(--spin, 180deg)'),
        opacity: 0,
        offset: 1,
      },
    ];

    const animation = element.animate(keyframes, {
      duration: sprite.durationMs,
      iterations: Infinity,
      easing: 'linear',
    });

    animation.currentTime = sprite.initialOffsetMs;
    animation.playbackRate = this.boosted ? BOOSTED_PLAYBACK_RATE : BASE_PLAYBACK_RATE;

    return animation;
  }

  private updatePlaybackRate(): void {
    const rate = this.boosted ? BOOSTED_PLAYBACK_RATE : BASE_PLAYBACK_RATE;
    this.animations.forEach(animation => {
      animation.playbackRate = rate;
    });
  }

  private destroyAnimations(): void {
    this.animations.forEach(animation => animation.cancel());
    this.animations = [];
  }

  private createSprites(kind: Sprite['kind'], count: number): Sprite[] {
    return Array.from({ length: count }).map((_, index) => {
      const durationMs =
        kind === 'flake' ? (Math.random() * 8 + 14) * 1000 : (Math.random() * 12 + 10) * 1000;
      const scale =
        kind === 'flake'
          ? Math.random() * 0.4 + 0.2 // ~0.2 – 0.6 for small, crisp flakes
          : Math.random() * 0.6 + 0.4;
      const opacity =
        kind === 'flake' ? Math.random() * 0.35 + 0.6 : Math.random() * 0.6 + 0.3;
      return {
        id: `${kind}-${index}`,
        kind,
        styles: {
          '--x': `${Math.round(Math.random() * 100)}vw`,
          '--scale': scale.toFixed(2),
          '--opacity': opacity.toFixed(2),
          '--spin': `${(Math.random() * 360).toFixed(0)}deg`,
        },
        durationMs,
        initialOffsetMs: Math.random() * durationMs,
      };
    });
  }

  private rebuildSprites(kind: 'drum' | 'flake' | 'both'): void {
    if (kind === 'drum' || kind === 'both') {
      this.drumsticks = this.createSprites('drum', this.drumCount);
    }
    if (kind === 'flake' || kind === 'both') {
      this.snowflakes = this.createSprites('flake', this.snowCount);
    }
    this.scheduleReinit();
  }

  private normalizeCount(value: number | null | undefined, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return fallback;
    }
    return Math.max(0, Math.floor(parsed));
  }
}
